import { ref, shallowRef } from "vue";
import liff from "@line/liff";

export interface LiffProfile {
  userId: string;
  displayName: string;
  pictureUrl?: string;
  statusMessage?: string;
}

export type LiffStatus = "idle" | "loading" | "ready" | "not-in-line" | "error";

const LIFF_ID = import.meta.env.VITE_LIFF_ID as string | undefined;
const DEV = import.meta.env.DEV;
// Opt-in flag for staged production builds (e.g. preview / test deploys without
// a real LIFF channel). Mock NEVER activates if VITE_LIFF_ID is set — that
// invariant is the production safety guard. See CLAUDE.md LOCKED RULE.
const ENABLE_MOCK =
  !LIFF_ID && (DEV || import.meta.env.VITE_LIFF_ENABLE_MOCK === "true");
const MOCK_USER_ID_KEY = "liff:mock:userId";

function getOrCreateMockUserId(): string {
  const fixed = import.meta.env.VITE_LIFF_MOCK_USER_ID as string | undefined;
  if (fixed) return fixed;
  const cached = localStorage.getItem(MOCK_USER_ID_KEY);
  if (cached) return cached;
  const id = "U" + Math.random().toString(36).slice(2, 18).padEnd(16, "0");
  localStorage.setItem(MOCK_USER_ID_KEY, id);
  return id;
}

/** Wipe the mock identity so the next init creates a fresh "new user". */
export function resetMockUser(): void {
  localStorage.removeItem(MOCK_USER_ID_KEY);
  // Also clear any draft tied to the previous identity.
  for (let i = sessionStorage.length - 1; i >= 0; i--) {
    const k = sessionStorage.key(i);
    if (k && k.startsWith("register:draft:")) sessionStorage.removeItem(k);
  }
}

const status = ref<LiffStatus>("idle");
const profile = shallowRef<LiffProfile | null>(null);
const idToken = ref<string | null>(null);
const error = ref<string | null>(null);

let initPromise: Promise<void> | null = null;

async function realInit(): Promise<void> {
  if (!LIFF_ID) throw new Error("VITE_LIFF_ID is not set");

  // Fetch Liff Mode from config
  let isDevelop = false;
  try {
    const apiUrl = import.meta.env.VITE_API_URL || "";
    const configRes = await fetch(`${apiUrl}/api/v1/public/config/liff-mode`);
    if (configRes.ok) {
      const data = await configRes.json();
      isDevelop = data.mode === "develop";
    }
  } catch (err) {
    console.error("Failed to fetch liff mode config", err);
  }

  await liff.init({ liffId: LIFF_ID, withLoginOnExternalBrowser: isDevelop });

  if (!isDevelop && !liff.isInClient()) {
    status.value = "not-in-line";
    return;
  }

  if (!liff.isLoggedIn()) {
    liff.login({
      redirectUri:
        typeof window !== "undefined" ? window.location.href : undefined,
    });
    return;
  }
  const p = await liff.getProfile();
  profile.value = {
    userId: p.userId,
    displayName: p.displayName,
    pictureUrl: p.pictureUrl,
    statusMessage: p.statusMessage,
  };
  idToken.value = liff.getIDToken();
  status.value = "ready";
}

function mockInit(): void {
  // Dev fallback so the register flow can be exercised outside LINE.
  // Token format: `mock:<userId>` — BE recognizes this prefix in dev mode only.
  const mockId = getOrCreateMockUserId();
  profile.value = {
    userId: mockId,
    displayName: "Dev Tester",
    pictureUrl: undefined,
  };
  idToken.value = `mock:${mockId}`;
  status.value = "ready";
   
  console.info(
    "%c[LIFF MOCK]%c userId=%s — call resetMockUser() to start fresh.",
    "background:#06c755;color:#fff;padding:2px 6px;border-radius:4px",
    "color:#06c755",
    mockId,
  );
}

export function useLiff() {
  function init(): Promise<void> {
    if (initPromise) return initPromise;
    status.value = "loading";
    initPromise = (async () => {
      try {
        if (ENABLE_MOCK) {
          mockInit();
          return;
        }
        await realInit();
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        error.value = msg;
        status.value = "error";
        // In dev (or explicitly enabled), fall back to mock so init failures
        // don't block local testing.
        if (ENABLE_MOCK) mockInit();
      }
    })();
    return initPromise;
  }

  function logout(): void {
    if (LIFF_ID && liff.isLoggedIn()) liff.logout();
    profile.value = null;
    idToken.value = null;
    status.value = "idle";
    initPromise = null;
  }

  function isMock(): boolean {
    return ENABLE_MOCK;
  }

  return { init, logout, status, profile, idToken, error, isMock };
}
